import { useReducer, Reducer, useRef } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import { addPhoneNumber, addPhoneVerificationToken } from 'actions/user';
import { postMessageToMobileApp } from 'components/app/helpers';
import { IS_INDIAN_EXCHANGE } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { ENABLE_AUTO_MOBILE_OTP_VERIFICATION } from 'constants/featureFlag';
import { getRequestOtpServerErrorMessage } from 'helpers/phoneVerification';
import {
  parseErrorMsg,
  validateIndianPhoneNumber,
  validatePhoneNumber,
} from 'helpers/utils';
import { useAutoMobileOTPVerification } from 'hooks/useAutoMobileOTPVerification';
import { getUserId } from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';

interface EnterPhoneState {
  apiStatus: 'success' | 'loading' | 'error' | 'initial';
  errorMessage: string | null;
}

export enum EnterPhoneActionTypes {
  API_STATUS_UPDATE = 'API_STATUS_UPDATE',
  ERROR_UPDATE = 'ERROR_UPDATE',
}
type EnterPhoneActions =
  | {
      type: EnterPhoneActionTypes.API_STATUS_UPDATE;
      payload: {
        value: EnterPhoneState['apiStatus'];
      };
    }
  | {
      type: EnterPhoneActionTypes.ERROR_UPDATE;
      payload: {
        message: EnterPhoneState['errorMessage'];
      };
    };

const initialState: EnterPhoneState = {
  apiStatus: 'initial',
  errorMessage: null,
};
const reducer: Reducer<EnterPhoneState, EnterPhoneActions> = (state, action) => {
  switch (action.type) {
    case EnterPhoneActionTypes.API_STATUS_UPDATE:
      return { ...state, apiStatus: action.payload.value };
    case EnterPhoneActionTypes.ERROR_UPDATE:
      return { ...state, errorMessage: action.payload.message, apiStatus: 'error' };
    default:
      return state;
  }
};

interface HookArgs {
  phone: number;
  formattedCountryCode: string;
  country: string | undefined;
  onSuccessCallBack: () => void;
  onErrorCallBack: (error: unknown) => void;
}
const useEnterPhone = ({
  phone,
  formattedCountryCode,
  country,
  onSuccessCallBack,
  onErrorCallBack,
}: HookArgs) => {
  const [enterPhoneState, enterPhoneDispatch] = useReducer(reducer, initialState);
  const userId = useAppSelector(getUserId);
  const { hashCode } = useAutoMobileOTPVerification();

  const { t } = useTranslation(['account', 'errors']);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const isPhoneNoValid = IS_INDIAN_EXCHANGE
    ? validateIndianPhoneNumber(phone)
    : validatePhoneNumber(phone);

  const dispatch = useAppDispatch();

  const verifyPhoneNumber = async () => {
    let data = {
      userId,
      country_calling_code: Number(formattedCountryCode),
      phone_number: phone,
    };

    if (ENABLE_AUTO_MOBILE_OTP_VERIFICATION && hashCode) {
      data = { ...data, auto_retrieval_otp_code: hashCode };
    }

    if (!formattedCountryCode || !phone || !country) {
      enterPhoneDispatch({
        type: EnterPhoneActionTypes.ERROR_UPDATE,
        payload: {
          message: t('errors:utils.somethingWentWrong'),
        },
      });
      return;
    }
    try {
      enterPhoneDispatch({
        type: EnterPhoneActionTypes.API_STATUS_UPDATE,
        payload: {
          value: 'loading',
        },
      });

      const response = await dispatch(addPhoneNumber(data));
      const phoneVerificationToken = response?.result;
      dispatch(addPhoneVerificationToken(phoneVerificationToken));

      if (ENABLE_AUTO_MOBILE_OTP_VERIFICATION) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.FETCH_OTP_FROM_SMS);
      }

      enterPhoneDispatch({
        type: EnterPhoneActionTypes.API_STATUS_UPDATE,
        payload: {
          value: 'success',
        },
      });

      onSuccessCallBack();
    } catch (err) {
      const { error } = parseErrorMsg(err);
      onErrorCallBack(error);

      enterPhoneDispatch({
        type: EnterPhoneActionTypes.ERROR_UPDATE,
        payload: {
          message: getRequestOtpServerErrorMessage(error, t),
        },
      });
      console.error(err);
    }
  };

  return {
    errorMessage: enterPhoneState.errorMessage,
    apiStatus: enterPhoneState.apiStatus,
    enterPhoneDispatch,
    recaptchaRef,
    verifyPhoneNumber,
    isPhoneNoValid,
  };
};

export default useEnterPhone;
